import Vue from 'vue'
import i18n from '@/i18n'
import { inboxService } from '@/helpers/services/inbox.service'
import { sessionHelper } from '@/utils'

/**
 * The waiting-queue alert, for the whole app rather than one screen.
 *
 * ── Why this is not in inbox.vue ─────────────────────────────────────────
 * It was, and that made the alert useless in the one case it exists for. An
 * agent is on Users, or Knowledge base, or has the tab in the background: the
 * poller lived in the inbox component, so leaving the inbox destroyed it and a
 * player could wait indefinitely with nothing making a sound. You only ever
 * heard the tone on the screen that was already showing you the queue.
 *
 * So the poller moved out of the component and into a module. One instance for
 * the session, started by the authenticated layout — which every signed-in page
 * renders inside — and stopped on sign-out. The inbox keeps its own 5-second
 * list poll for what it DISPLAYS; announcing is no longer its job, which is
 * also what stops the two of them beeping twice for one conversation.
 *
 * ── Why it polls when the tab is hidden ──────────────────────────────────
 * The inbox list poll skips `document.hidden` ticks, correctly: nobody is
 * reading a list they cannot see. This one must not, because a hidden tab is
 * precisely the situation the sound is for. Browsers allow audio from a
 * background tab once the context has been unlocked by a gesture, which is
 * what `unlock()` below arranges.
 */

/** Slower than the inbox's own 5s list poll — this one runs all shift, on
 *  every page, and 10s of extra wait on an alert costs nothing. */
const INTERVAL_MS = 10000

/** One request's worth of the queue. `meta.total` carries the real count, so
 *  the number shown is right even when there are more waiting than this. */
const PAGE_SIZE = 50

const SOUND_KEY = 'inbox_sound'

export const alertState = Vue.observable({
  /** Everyone waiting, brand-scoped by the server exactly like the inbox. */
  waiting: 0,
  /** The newest conversation that triggered an alert, for the in-app banner.
   *  Null once dismissed or once the agent opens the inbox. */
  fresh: null,
  /** How many arrived in the same tick, so the banner can say "and 3 more". */
  freshExtra: 0,
  soundOn: localStorage.getItem(SOUND_KEY) !== 'off',
})

let timer = null
let announced = new Set()
let seeded = false
let ctx = null
let unlockBound = false

/**
 * A shared AudioContext, resumed rather than rebuilt.
 *
 * Built lazily on the first gesture or the first beep, whichever comes first.
 * Per-beep construction worked on the inbox screen because the agent had
 * certainly clicked something there; on a background tab there may have been no
 * gesture on THIS page at all, and a context created in that state starts
 * `suspended` and stays silent. One long-lived context, unlocked once, survives
 * every later alert.
 */
function audio() {
  const Ctx = window.AudioContext || window.webkitAudioContext
  if (!Ctx) return null
  if (!ctx) ctx = new Ctx()
  if (ctx.state === 'suspended') ctx.resume().catch(() => {})
  return ctx
}

/** Autoplay policy needs one real gesture. Take the first one that happens
 *  anywhere in the app, then stop listening. */
function unlock() {
  if (unlockBound) return
  unlockBound = true
  const once = () => {
    audio()
    window.removeEventListener('pointerdown', once)
    window.removeEventListener('keydown', once)
  }
  window.addEventListener('pointerdown', once)
  window.addEventListener('keydown', once)
}

/**
 * A short tone, synthesised rather than loaded.
 *
 * No audio file to host, nothing to 404, and it works offline.
 *
 * Three rising pulses rather than one soft tone. The first version was a single
 * 880 Hz sine at 0.18 gain and it was being missed. Three things make this one
 * carry across a room:
 *
 *   pattern     a repeated pulse reads as an ALARM; one tone reads as a UI
 *               click. The biggest factor by far — ears filter out single
 *               blips, not rhythms.
 *   waveform    square has odd harmonics all the way up, so it cuts through
 *               speech and keyboard noise that a pure sine sits underneath.
 *   pitch       ~1 kHz is where human hearing is most sensitive, and a rising
 *               interval reads as "attend to this" where a falling one reads
 *               as "finished".
 *
 * Still bounded at ~0.6s: an alert that outstays its welcome gets muted within
 * a day, and then it protects nobody.
 */
export function beep() {
  if (!alertState.soundOn) return
  try {
    const c = audio()
    if (!c) return

    const out = c.createGain()
    out.connect(c.destination)
    // One master gain, so the three pulses cannot stack into a clipped crack
    // if the timings ever overlap.
    out.gain.value = 0.9

    const pulses = [
      { at: 0.0, hz: 988 },
      { at: 0.18, hz: 1319 },
      { at: 0.36, hz: 1568 },
    ]

    for (const p of pulses) {
      const t = c.currentTime + p.at
      const osc = c.createOscillator()
      const g = c.createGain()
      osc.type = 'square'
      osc.frequency.value = p.hz
      osc.connect(g)
      g.connect(out)

      // Ramped, never switched: a square wave started at full gain clicks, and
      // the click is what makes it sound broken rather than loud.
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(0.5, t + 0.012)
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15)

      osc.start(t)
      osc.stop(t + 0.16)
    }
    // The context is deliberately NOT closed: closing it would mean the next
    // alert has to unlock all over again, from a page the agent may not have
    // touched. The nodes are disconnected by garbage collection.
  } catch (e) {
    /* an alert that throws must not take the app with it */
  }
}

export function toggleSound() {
  alertState.soundOn = !alertState.soundOn
  localStorage.setItem(SOUND_KEY, alertState.soundOn ? 'on' : 'off')
  if (alertState.soundOn) {
    beep()
    if (window.Notification && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }
}

export function dismiss() {
  alertState.fresh = null
  alertState.freshExtra = 0
}

/**
 * Alert on genuinely new waiting conversations.
 *
 * The first poll seeds the set without alerting — otherwise signing in to a
 * queue of nine plays nine tones and tells the agent nothing the screen does
 * not already say.
 *
 * ── Why `seeded` and not "the list is empty" ────────────────────────────
 * These are the same thing ONLY when the app opens with a queue already in it.
 * Open on an EMPTY queue — a quiet shift, a fresh install — and "is empty"
 * stays true until the first conversation arrives, so the first real handoff of
 * the day gets treated as seed data and swallowed. Exactly the alert that
 * matters most.
 *
 * ── Why the set forgets ─────────────────────────────────────────────────
 * A conversation that is resolved and comes back hours later with a new problem
 * is new work, and would never alert again while its uuid sat in the set.
 * Because this poll asks for `status=waiting` only, absence from the response IS
 * the evidence that it is no longer waiting — but only when the response holds
 * the whole set. Past PAGE_SIZE waiting rows the page is a window, not the set,
 * so nothing is forgotten that tick rather than re-alerting half the queue.
 */
function announce(rows, total) {
  const seeding = !seeded
  seeded = true

  const seen = new Set()
  const fresh = []
  for (const c of rows) {
    seen.add(c.uuid)
    if (announced.has(c.uuid)) continue
    announced.add(c.uuid)
    if (!seeding) fresh.push(c)
  }

  if (total <= rows.length) {
    for (const uuid of announced) if (!seen.has(uuid)) announced.delete(uuid)
  }

  if (!fresh.length) return

  alertState.fresh = fresh[0]
  alertState.freshExtra = fresh.length - 1
  beep()

  if (window.Notification && Notification.permission === 'granted') {
    const n = fresh[0]
    new Notification(i18n.t('inbox.waitingNotice', [n.contact.label]), {
      body: n.handover_reason || i18n.t('inbox.needsHuman'),
      tag: n.uuid,
    })
  }
}

async function tick() {
  // A poll that outlives the token would 401 every ten seconds forever. The
  // layout stops this on sign-out; this is the backstop for a session that
  // expires while the tab sits open.
  if (!sessionHelper.hasAuthToken()) return stop()
  try {
    const res = await inboxService.getConversations({
      status: 'waiting',
      pageSize: PAGE_SIZE,
    })
    const rows = res.data || []
    const total = res.meta ? res.meta.total : rows.length
    alertState.waiting = total
    announce(rows, total)
  } catch (e) {
    // A failed poll is not worth surfacing: the next one is ten seconds away
    // and an error banner on every page would be worse than a late alert.
  }
}

/**
 * Mounted-layout count, and the reason start/stop are not called directly.
 *
 * Vue Router patches a route change by creating the incoming component BEFORE
 * destroying the outgoing one. A naive `created() { start() }` /
 * `beforeDestroy() { stop() }` therefore runs start-then-STOP on every
 * navigation, and the poller is dead from the first time the agent clicks a
 * menu item -- silently, because the first page load looks perfect.
 *
 * Counting mounts instead, the sequence across a navigation is 1 -> 2 -> 1 and
 * never reaches zero, so the poll runs unbroken. It reaches zero exactly once:
 * sign-out, when the authenticated layout unmounts and nothing replaces it.
 */
let mounted = 0

export function attach() {
  mounted += 1
  if (timer) return
  unlock()
  tick()
  timer = setInterval(tick, INTERVAL_MS)
}

export function detach() {
  mounted = Math.max(0, mounted - 1)
  if (mounted === 0) stop()
}

export function stop() {
  clearInterval(timer)
  timer = null
  announced = new Set()
  seeded = false
  alertState.waiting = 0
  dismiss()
}

export const inboxAlert = { state: alertState, attach, detach, stop, beep, toggleSound, dismiss }
